<template>
  <div class="media-wrapper">
    <!-- Toggle button -->
    <button
      class="toggle-overlay-btn"
      :style="{ right: overlayVisible ? '260px' : '10px' }"
      @click="toggleOverlay"
    >
      <span v-if="overlayVisible">⮜</span>
      <span v-else>⮞</span>
    </button>

    <!-- Main focus view -->
    <div class="primary-view">
      <!-- Show avatar for audio calls -->
      <img
        v-if="focusedItem.type === 'audio'"
        :src="focusedItem.avatar"
        class="avatar"
        alt="Audio Avatar"
      />
      <audio
        v-else-if="focusedItem.type === 'audio'"
        :src="focusedItem.src"
        ref="focusedAudio"
        class="focused-audio"
      ></audio>
      <!-- Webpage view -->
      <iframe
        v-else-if="focusedItem.type === 'webpage'"
        :src="focusedItem.src"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        allowfullscreen
      ></iframe>
      <!-- Video or stream view -->
      <video
        v-else-if="focusedItem.type === 'videoFile' || focusedItem.type === 'stream'"
        :src="focusedItem.src"
        controls
        autoplay
      ></video>
    </div>

    <!-- Overlay menu -->
    <div class="video-grid-overlay" :class="{ collapsed: !overlayVisible }">
      <!-- Expanded tiles -->
      <div v-if="overlayVisible">
        <div
          v-for="(item, i) in mediaItems"
          :key="i"
          class="video-tile"
          ref="tiles"
          v-bind="draggables[i]"
        >
          <!-- Tile content -->
          <img
            v-if="item.type === 'audio'"
            :src="item.avatar || 'https://via.placeholder.com/150/007BFF/ffffff?text=Audio'"
            class="tile-avatar"
            alt="Avatar"
          />
          <audio
                v-else-if="item.type === 'audio'"
                :src="item.src"
                ref="tileAudios"
                class="tile-audio"
            ></audio>
          <iframe
            v-else-if="item.type === 'webpage'"
            :src="item.src"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            allowfullscreen
          ></iframe>
          <video
            v-else
            :src="item.src"
            muted
          ></video>

          <!-- Transparent overlay to capture focus click -->
          <div class="tile-overlay" @click="focusItem(item)"></div>

          <!-- Icon bar for non-webpage -->
          <div v-if="item.type !== 'webpage'" class="icon-bar">
            <button :class="{ active: isMuted[i] }" @click.stop="toggleMute(i)">
              <i :class="['fa', isMuted[i] ? 'fa-microphone-slash' : 'fa-microphone']"></i>
            </button>
            <button :class="{ active: isPlaying[i] }" @click.stop="togglePlay(i)">
              <i :class="['fa', isPlaying[i] ? 'fa-pause' : 'fa-play']"></i>
            </button>
            <button @click.stop="toggleFullscreenTile(i)">
              <i class="fa fa-expand"></i>
            </button>
            <div class="dropdown">
              <button class="dropdown-toggle" @click.stop="toggleDropdown(i)">
                <i class="fa fa-ellipsis-h"></i>
              </button>
              <ul v-if="dropdownOpen[i]" class="dropdown-menu">
                <li @click.stop="option1(i)">Option 1</li>
                <li @click.stop="option2(i)">Option 2</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <!-- Collapsed initials view -->
      <div v-else>
        <div
          v-for="(item, i) in mediaItems"
          :key="i"
          class="initial-tile"
          @click="focusItem(item); toggleOverlay()"
        >
          {{ getInitials(item.src) }}
        </div>
      </div>
    </div>

    <!-- Global controls (hidden) -->
    <div class="global-controls" style="display:none;">
      <button @click="reloadAll">Reload All</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useDraggable } from '@vueuse/core';

export default {
  name: 'MediaGrid',
  setup() {
    const inputs = [
      { src: 'https://www.wikipedia.org/', type: 'webpage' },
      { src: 'https://www.w3schools.com/html/movie.mp4', type: 'videoFile' },
      { src: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4', type: 'stream' },
      { src: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3', type: 'audio', avatar: 'https://avatar.iran.liara.run/public' }
    ];

    const mediaItems = ref(inputs);
    const focusedItem = ref(inputs[0]);
    const overlayVisible = ref(true);
    const isMuted = ref(inputs.map(() => false));
    const isPlaying = ref(inputs.map(() => false));
    const dropdownOpen = ref(inputs.map(() => false));
    const tiles = ref([]);
    const draggables = ref([]);

    onMounted(() => {
      draggables.value = tiles.value.map(el => useDraggable(el));
      tiles.value.forEach((tile, i) => {
        const video = tile.querySelector('video');
        const audio = tile.querySelector('audio.tile-audio');
        if (video) {
          video.onplay = () => (isPlaying.value[i] = true);
          video.onpause = () => (isPlaying.value[i] = false);
        }
        if (audio) {
          audio.onplay = () => (isPlaying.value[i] = true);
          audio.onpause = () => (isPlaying.value[i] = false);
        }
      });
    });

    const focusItem = item => (focusedItem.value = item);
    const toggleOverlay = () => (overlayVisible.value = !overlayVisible.value);
    const getInitials = src =>
      src
        ? new URL(src).host.replace('www.', '').split('.').map(p => p[0].toUpperCase()).join('')
        : '';

    const reloadAll = () => {
      tiles.value.forEach((tile, i) => {
        const item = mediaItems.value[i];
        const el = tile.querySelector(item.type === 'webpage' ? 'iframe' : 'video');
        if (item.type === 'webpage') el.src = el.src;
        else { el.currentTime = 0; el.play(); }
      });
    };

    const toggleMute = i => {
      const video = tiles.value[i].querySelector('video');
      const audio = tiles.value[i].querySelector('audio.tile-audio');
      if (video) {
        video.muted = !video.muted;
        isMuted.value[i] = video.muted;
      } else if (audio) {
        audio.muted = !audio.muted;
        isMuted.value[i] = audio.muted;
      }
    };

    const togglePlay = i => {
      const video = tiles.value[i].querySelector('video');
      const audio = tiles.value[i].querySelector('audio.tile-audio');
      if (video) {
        video.paused ? video.play() : video.pause();
      } else if (audio) {
        audio.paused ? audio.play() : audio.pause();
      }
    };

    const toggleFullscreenTile = i => {
      const el = tiles.value[i];
      !document.fullscreenElement ? el.requestFullscreen() : document.exitFullscreen();
    };

    const toggleDropdown = i => {
      dropdownOpen.value[i] = !dropdownOpen.value[i];
    };
    const option1 = i => (dropdownOpen.value[i] = false);
    const option2 = i => (dropdownOpen.value[i] = false);

    return {
      mediaItems,
      focusedItem,
      overlayVisible,
      isMuted,
      isPlaying,
      dropdownOpen,
      tiles,
      draggables,
      focusItem,
      toggleOverlay,
      getInitials,
      reloadAll,
      toggleMute,
      togglePlay,
      toggleFullscreenTile,
      toggleDropdown,
      option1,
      option2
    };
  }
};
</script>

<style scoped>
.media-wrapper { display: flex; flex-direction: column; height: 100vh; background: white; position: relative; }
.toggle-overlay-btn { position: absolute; top: 10px; z-index: 4; background: #333; color: #fff; border: none; border-radius: 4px; padding: 6px 10px; cursor: pointer; transition: right 0.3s; }
.primary-view { flex: 1; position: relative; background: #eee; overflow: hidden; display: flex; justify-content: center; align-items: center; }
.primary-view iframe, .primary-view video { width: 100%; height: 100%; object-fit: contain; border: none; }
.primary-view .avatar { width: 150px; height: 150px; border-radius: 50%; object-fit: cover; }
.video-grid-overlay { position: absolute; top: 0; right: 0; width: 250px; height: 100%; background: rgba(255,255,255,0.95); display: flex; flex-direction: column; overflow-y: auto; padding: 10px; box-shadow: -2px 0 8px rgba(0,0,0,0.1); z-index: 2; transition: width 0.3s ease, padding 0.3s ease; }
.video-grid-overlay.collapsed { width: 40px; padding: 5px; }
.video-tile { position: relative; width: 100%; height: 120px; margin-bottom: 10px; border: 1px solid #ccc; border-radius: 8px; overflow: hidden; background: #fff; cursor: pointer; transition: transform 0.2s; }
.video-tile:hover { transform: scale(1.03); }
.video-tile iframe, .video-tile video, .tile-avatar { width: 100%; height: 100%; object-fit: cover; }
.tile-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; cursor: pointer; z-index: 3; }
.icon-bar { position: absolute; bottom: 0; left: 0; width: 100%; display: flex; justify-content: space-around; background: rgba(0,0,0,0.6); padding: 5px 0; border-top: 1px solid rgba(255,255,255,0.2); z-index: 4; }
.icon-bar button { background: none; border: none; color: white; font-size: 18px; cursor: pointer; }
.icon-bar button.active i { color: red; }
.dropdown { position: relative; }
.dropdown-menu { position: absolute; top: 24px; right: 0; background: #fff; border: 1px solid #ccc; border-radius: 4px; list-style: none; padding: 5px 0; margin: 0; box-shadow: 0 2px 8px rgba(0,0,0,0.15); z-index: 5; }
.dropdown-menu li { padding: 5px 12px; cursor: pointer; }
.dropdown-menu li:hover { background: #f0f0f0; }
.initial-tile { display: flex; align-items: center; justify-content: center; width: 100%; height: 40px; margin-bottom: 5px; border: 1px solid #ccc; border-radius: 8px; background: #f7f7f7; color: #333; font-weight: bold; cursor: pointer; }
.global-controls { position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); background: #f0f0f0; padding: 8px 12px; border-radius: 6px; display: flex; gap: 10px; z-index: 3; }
.global-controls button { padding: 6px 12px; background: #333; color: #fff; border: none; border-radius: 4px; cursor: pointer; }
</style>
